import React, { useState, useEffect, useRef, useCallback } from 'react';
import { JSONPath } from 'jsonpath-plus';
import { Box, Paper, Typography, TextField, Alert } from '@mui/material';
import MonacoEditor from '../../components/MonacoEditor.js';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const sampleJson = {
  "store": {
    "book": [
      { "category": "reference", "author": "Nigel Rees", "title": "Sayings of the Century", "price": 8.95 },
      { "category": "fiction", "author": "Evelyn Waugh", "title": "Sword of Honour", "price": 12.99 },
      { "category": "fiction", "author": "Herman Melville", "title": "Moby Dick", "isbn": "0-553-21311-3", "price": 8.99 },
      { "category": "fiction", "author": "J. R. R. Tolkien", "title": "The Lord of the Rings", "isbn": "0-395-19395-8", "price": 22.99 }
    ],
    "bicycle": { "color": "red", "price": 19.95 }
  }
};

function JsonPathTester() {
  const [jsonInput, setJsonInput] = useState(JSON.stringify(sampleJson, null, 2));
  const [query, setQuery] = useState('$.store.book[*].author');
  const [result, setResult] = useState('');
  const [error, setError] = useState(null);

  const editorRef = useRef(null);

  const evaluatePath = useCallback(() => {
    try {
      if (!jsonInput.trim()) {
        setResult('');
        setError(null);
        return;
      }
      const parsedJson = JSON.parse(jsonInput);
      const output = JSONPath({ path: query, json: parsedJson });
      setResult(JSON.stringify(output, null, 2));
      setError(null);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError('Invalid JSON input.');
      } else {
        setError(`JSONPath Error: ${e.message}`);
      }
      setResult('');
    }
  }, [jsonInput, query]);

  useEffect(() => {
    evaluatePath();
  }, [evaluatePath]);

  const handleResize = useCallback(() => {
    setTimeout(() => editorRef.current?.layout(), 0);
  }, []);

  return (
    <>
      <SEO
        title="JSONPath Tester | Evaluate JSONPath Online | UmbrellaCo"
        description="Test and evaluate JSONPath expressions instantly. Free online tool to extract specific data from JSON objects using JSONPath syntax."
        keywords="jsonpath tester, evaluate jsonpath, jsonpath online, json path evaluator, extract json data"
        url="/json-tools/jsonpath"
        jsonLd={createSoftwareAppSchema(
          'JSONPath Tester',
          'Test and evaluate JSONPath queries against JSON data.',
          'https://umbrellaco.in/json-tools/jsonpath',
          ['JSONPath Evaluation', 'Data Extraction', 'Developer Tools']
        )}
      />
      <ToolPageContainer
        title="JSONPath Tester"
        description="Write JSONPath expressions to evaluate and extract data from your JSON documents instantly."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <TextField
          label="JSONPath Expression"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          placeholder="e.g., $.store.book[*].author"
        />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>

      <Box sx={{ flexGrow: 1, px: 2, pb: 2, minHeight: 0 }}>
        <PanelGroup direction="horizontal" onLayout={handleResize}>
          <Panel defaultSize={50} minSize={30}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>JSON Document</Typography>
              <Paper variant="outlined" sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
                <MonacoEditor
                  ref={editorRef}
                  language="json"
                  value={jsonInput}
                  onChange={setJsonInput}
                />
              </Paper>
            </Box>
          </Panel>
          <PanelResizeHandle style={{ width: '16px' }} />
          <Panel defaultSize={50} minSize={30}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Evaluation Result (Array)</Typography>
              <Paper variant="outlined" sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
                <MonacoEditor
                  language="json"
                  value={result}
                  options={{ readOnly: true }}
                />
              </Paper>
            </Box>
          </Panel>
        </PanelGroup>
      </Box>
      </Box>
    </ToolPageContainer>
    </>
  );
}

export default JsonPathTester;
